import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { Popover } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import Button, { ButtonSize } from 'packages/kakapo-ui-kit/components/editors/Button';
import useFeature from 'packages/kakapo-ui-application/hooks/useFeature';
import HorizontalRule from 'packages/kakapo-ui-kit/components/common/HorizontalRule';
import Label from 'packages/kakapo-ui-kit/components/forms/controls/Label';

import BulkArchiveUserModal from './BulkArchiveUserModal';
import BulkAssignCycleModal from './BulkAssignCycleModal';
import BulkRemoveCycleModal from './BulkRemoveCycleModal';
import BulkExportDataModal from './BulkExportDataModal';
import BulkResendActivationMailModal from './BulkResendActivationMailModal';
import BulkRestoreUserModal from './BulkRestoreUserModal';
import BulkSingleSignOnModal from './BulkSingleSignOnModal';
import BulkUpdateActivationDateModal from './BulkUpdateActivationDateModal';

function EmployeeBulkActions(props: { onAction: (action: string) => void; onBlur: () => void }) {
	const { t } = useTranslation();
	const cyclesEnabled = useFeature('2021.cycles');

	return (
		<Box ref={(ref) => ref?.focus()} pb={10} tabIndex={-1} onBlur={() => props.onBlur()}>
			<Box px={10} pt={10}>
				<Label title={t('employees.generalActions.title')} />
			</Box>
			<Box px={10}>
				<Button
					block
					onMouseDown={(event) => event.preventDefault()}
					onClick={() => props.onAction('updateActivationDate')}
					size={ButtonSize.Medium}
				>
					{t('employees.updateActivationDate.title')}
				</Button>
			</Box>
			<Flex px={10} pt={10}>
				<Box mr={1} flexGrow={1} flexBasis={0}>
					<Button
						block
						onMouseDown={(event) => event.preventDefault()}
						onClick={() => props.onAction('archive')}
						size={ButtonSize.Medium}
					>
						{t('employees.archive.title')}
					</Button>
				</Box>

				<Box ml={1} flexGrow={1} flexBasis={0}>
					<Button
						block
						onMouseDown={(event) => event.preventDefault()}
						onClick={() => props.onAction('restore')}
						size={ButtonSize.Medium}
					>
						{t('employees.restore.title')}
					</Button>
				</Box>
			</Flex>
			<>
				<Box mt={10}>
					<HorizontalRule />
				</Box>

				<Box px={10} pt={10}>
					<Label title={t('employees.loginActions.title')} />
				</Box>

				<Box px={10}>
					<Button
						block
						onMouseDown={(event) => event.preventDefault()}
						onClick={() => props.onAction('resendActivationMail')}
						size={ButtonSize.Medium}
					>
						{t('employees.resendActivationMail.title')}
					</Button>
				</Box>

				<Box px={10} pt={10}>
					<Button
						block
						onMouseDown={(event) => event.preventDefault()}
						onClick={() => props.onAction('singleSignOn')}
						size={ButtonSize.Medium}
					>
						{t('employees.toggleSingleSignOn.title')}
					</Button>
				</Box>
			</>
			{cyclesEnabled && (
				<>
					<Box mt={10}>
						<HorizontalRule />
					</Box>

					<Box px={10} pt={10}>
						<Label title={t('employees.cycleActions.title')} />
					</Box>

					<Flex px={10}>
						<Box mr={1} flexGrow={1} flexBasis={0}>
							<Button
								block
								onMouseDown={(event) => event.preventDefault()}
								onClick={() => props.onAction('assignCycle')}
								size={ButtonSize.Medium}
							>
								{t('employees.assignCycle.title')}
							</Button>
						</Box>

						<Box ml={1} flexGrow={1} flexBasis={0}>
							<Button
								block
								onMouseDown={(event) => event.preventDefault()}
								onClick={() => props.onAction('removeCycle')}
								size={ButtonSize.Medium}
							>
								{t('employees.removeCycle.title')}
							</Button>
						</Box>
					</Flex>
				</>
			)}

			<>
				<Box mt={10}>
					<HorizontalRule />
				</Box>

				<Box px={10} pt={10}>
					<Label title={t('employees.advancedActions.title')} />
				</Box>

				<Flex px={10}>
					<Box ml={1} flexGrow={1} flexBasis={0}>
						<Button
							block
							onMouseDown={(event) => event.preventDefault()}
							onClick={() => props.onAction('exportData')}
							size={ButtonSize.Medium}
						>
							{t('employees.exportData.title')}
						</Button>
					</Box>
				</Flex>
			</>
		</Box>
	);
}

function EmployeesBulkModalComponent(modal: string) {
	switch (modal) {
		case 'archive':
			return BulkArchiveUserModal;
		case 'assignCycle':
			return BulkAssignCycleModal;
		case 'removeCycle':
			return BulkRemoveCycleModal;
		case 'resendActivationMail':
			return BulkResendActivationMailModal;
		case 'restore':
			return BulkRestoreUserModal;
		case 'singleSignOn':
			return BulkSingleSignOnModal;
		case 'updateActivationDate':
			return BulkUpdateActivationDateModal;
		case 'exportData':
			return BulkExportDataModal;
		default:
			throw new Error('Unknown bulk action.');
	}
}

function EmployeesBulkModal(props: {
	modal: string;
	users: graphql.User[];
	onDismiss: () => void;
}) {
	const Component = EmployeesBulkModalComponent(props.modal);
	return <Component users={props.users} onDismiss={props.onDismiss} />;
}

export default function EmployeesBulkButton(props: { selection: graphql.User[] }) {
	const { t } = useTranslation();

	const [menu, setMenu] = useState(false);

	const [modal, setModal] = useState<string | undefined>();

	return (
		<>
			<Popover.Target
				visible={menu}
				contents={
					<EmployeeBulkActions
						onAction={(action) => {
							setModal(action);
							setMenu(false);
						}}
						onBlur={() => setMenu(false)}
					/>
				}
			>
				<Button onClick={() => setMenu(true)} size={ButtonSize.Medium}>
					{t('employees.bulkActions.title')} ({props.selection.length})
				</Button>
			</Popover.Target>
			{modal && (
				<EmployeesBulkModal
					modal={modal}
					users={props.selection}
					onDismiss={() => setModal(undefined)}
				/>
			)}
		</>
	);
}
